import { Nullable } from "@core/domain/types/nullable.type";
import { ContractTerminationDto } from "@entity/data/dto/employee/contracts/termination/contract-termination.dto";
import { MedicalLeavePeriodMapper } from "@entity/data/mappers/employee/contracts/termination/medical-leave-period.mapper";
import { MedicalLeaveSubtypeEnumMapper } from "@entity/data/mappers/employee/medical-leave-subtype-enum.mapper";
import { TerminationCategoryEnumMapper } from "@entity/data/mappers/employee/termination-category-enum.mapper";
import { TerminationReasonEnumMapper } from "@entity/data/mappers/employee/termination-reason-enum.mapper";
import { ContractTermination } from "@entity/domain/models/employee/contracts/termination/contract-termination.model";
import { validateSync } from "class-validator";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";

@injectable()
export class ContractTerminationMapper {
    constructor(
        @inject(TerminationReasonEnumMapper)
        private readonly terminationReasonEnumMapper: TerminationReasonEnumMapper,
        @inject(TerminationCategoryEnumMapper)
        private readonly terminationCategoryEnumMapper: TerminationCategoryEnumMapper,
        @inject(MedicalLeaveSubtypeEnumMapper)
        private readonly medicalLeaveSubtypeEnumMapper: MedicalLeaveSubtypeEnumMapper,
        @inject(MedicalLeavePeriodMapper)
        private readonly medicalLeavePeriodMapper: MedicalLeavePeriodMapper,
    ) {}

    map(
        contractTerminationDto: ContractTerminationDto,
    ): Nullable<ContractTermination> {
        const errors = validateSync(contractTerminationDto);

        if (errors.length > 0) {
            console.error(errors);

            return null;
        }

        const type = contractTerminationDto.type
            ? this.terminationReasonEnumMapper.map(contractTerminationDto.type)
            : null;

        const terminationCategory = this.terminationCategoryEnumMapper.map(
            contractTerminationDto.termination_category,
        );
        if (!terminationCategory) return null;

        const medicalLeaveSubtype = contractTerminationDto.medical_leave_subtype
            ? this.medicalLeaveSubtypeEnumMapper.map(
                  contractTerminationDto.medical_leave_subtype,
              )
            : null;

        const medicalLeavePeriods = contractTerminationDto.medical_leave_periods
            ? contractTerminationDto.medical_leave_periods
                  .map((periodDto) =>
                      this.medicalLeavePeriodMapper.map(periodDto),
                  )
                  .filter((period) => period !== null)
            : [];

        const contractTermination = new ContractTermination(
            contractTerminationDto.id,
            terminationCategory,
            DateTime.fromISO(contractTerminationDto.start_date),
            contractTerminationDto.reason,
            type,
            contractTerminationDto.contract,
            medicalLeaveSubtype,
            contractTerminationDto.cost
                ? Number(contractTerminationDto.cost)
                : null,
            contractTerminationDto.total_cost
                ? Number(contractTerminationDto.total_cost)
                : null,
            contractTerminationDto.end_date
                ? DateTime.fromISO(contractTerminationDto.end_date)
                : null,
            medicalLeavePeriods as any[],
        );

        return contractTermination;
    }
}
